import { useLayoutEffect, useRef, useState } from "react";

/**
 * Returns a `ref` and a `height` string that makes the referenced element fill the space from its
 * own top down to the bottom of the viewport (minus its container's bottom padding).
 *
 * This replaces hardcoded `calc(100vh - <fixed>)` heights, which only matched one page's chrome and
 * overflowed the viewport on every other page (a scrollbar everywhere). By measuring the element's
 * actual position it fits exactly on any page, regardless of the chrome above it. Recomputes on
 * window resize and whenever surrounding layout changes height.
 *
 * @param enabled set false to skip measuring (e.g. when a fixed height is used instead).
 */
export function useFillHeight<T extends HTMLElement = HTMLDivElement>(enabled = true) {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState<string>();

  useLayoutEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    const recompute = () => {
      const top = el.getBoundingClientRect().top;
      const parent = el.parentElement;
      const padBottom = parent
        ? parseFloat(getComputedStyle(parent).paddingBottom) || 0
        : 0;
      const available = Math.floor(window.innerHeight - top - padBottom);
      setHeight(`${Math.max(available, 200)}px`);
    };

    recompute();
    window.addEventListener("resize", recompute);
    const ro = new ResizeObserver(recompute);
    ro.observe(document.body);
    return () => {
      window.removeEventListener("resize", recompute);
      ro.disconnect();
    };
  }, [enabled]);

  return { ref, height };
}

export default useFillHeight;
